import { useState } from 'react';
import { ShoppingBag, Calendar, Tag, RotateCcw, ExternalLink, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Item } from '@/lib/types';

export function WishlistArchivePage() {
  const { data, loading, error, refetch } = useApi<{ items: Item[] }>('/items?status=wishlist-archive');
  const { mutate, loading: busy } = useMutation();

  const items = data?.items ?? [];
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const handleRestore = async (id: string) => {
    setRestoringId(id);
    const res = await mutate('post', `/items/${id}/transition`, { action: 'restore-wishlist' });
    setRestoringId(null);
    if (res !== null) refetch();
  };

  return (
    <Page>
      <PageHeader section="Wishlist Archive" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <>
            <Card className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Archived Wishlist Items</h2>
                  <p className="text-xs text-muted-foreground">{items.length} items</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Items removed from your wishlist are stored here. You can restore them to your wishlist.
              </p>
            </Card>

            {items.length === 0 ? (
              <EmptyState
                icon={<ShoppingBag className="w-8 h-8" />}
                title="No archived items"
                description="Items you remove from your wishlist will appear here."
              />
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <Card key={item.id} className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground truncate">{item.name}</h3>
                        {item.brand && <p className="text-xs text-muted-foreground">{item.brand}</p>}
                      </div>
                      {item.cost != null && (
                        <span className="text-sm font-semibold text-foreground ml-2">
                          {formatCurrency(item.cost, item.currency)}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Tag className="w-3 h-3" />
                        <span>{item.category}</span>
                      </div>
                      {item.wishlistAddedDate && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>Added to wishlist: {formatDate(item.wishlistAddedDate)}</span>
                        </div>
                      )}
                      {item.wishlistArchivedAt && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Trash2 className="w-3 h-3" />
                          <span>Archived: {formatDate(item.wishlistArchivedAt)}</span>
                        </div>
                      )}
                      {item.wishlistArchiveReason && (
                        <div className="px-2 py-1 bg-secondary/50 rounded text-xs text-muted-foreground">
                          Reason: {item.wishlistArchiveReason}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-3 border-t border-border">
                      <Button
                        fullWidth
                        className="py-2 text-xs"
                        onClick={() => handleRestore(item.id)}
                        disabled={busy && restoringId === item.id}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {busy && restoringId === item.id ? 'Restoring…' : 'Restore'}
                      </Button>
                      {item.onlineStoreUrl && (
                        <a
                          href={item.onlineStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center px-3 py-2 bg-card border border-border rounded-xl text-xs font-medium text-foreground hover:border-primary/30 transition-all"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Main>
    </Page>
  );
}
